import * as React from "react";
import { Controller, useWatch } from "react-hook-form";
import { Label } from "../../../../components/ui/label";
import { Input } from "../../../../components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectLabel,
  SelectGroup,
} from "../../../../components/ui/select";
import { useEffect } from "react";
import { FieldError } from "../FieldError";
import { debounce } from "lodash";
import axios from "axios";

const YES_NO = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

export function USDotNumber({ control, register, trigger, errors, setValue }) {
  const dotNumber = useWatch({ control, name: "step2.dot.dotNumber" });
  const hasUsdot = useWatch({ control, name: "step2.dot.hasUsdot" });
  const carriers = useWatch({ control, name: "step1.carrier" }) ?? [];
  const requiresDot = Array.isArray(carriers) && carriers.includes("Cover Whale");

  // values we might backfill (so we can set only if empty)
  const insuredName = useWatch({ control, name: "step2.insuredName" });
  const dba = useWatch({ control, name: "step2.dba" });

  const mAddr1 = useWatch({ control, name: "step2.mailingAddress.address1" });
  const mCity = useWatch({ control, name: "step2.mailingAddress.city" });
  const mState = useWatch({ control, name: "step2.mailingAddress.state" });
  const mZip = useWatch({ control, name: "step2.mailingAddress.zip" });

  const gAddr1 = useWatch({ control, name: "step2.garagingAddress.address1" });
  const gCity = useWatch({ control, name: "step2.garagingAddress.city" });
  const gState = useWatch({ control, name: "step2.garagingAddress.state" });
  const gZip = useWatch({ control, name: "step2.garagingAddress.zip" });

  const stateOperation = useWatch({ control, name: "step8.stateOperation" });

  const isEmpty = (v) => v == null || (typeof v === "string" && v.trim() === "");
  const normalizeZip5 = (z) => (z ?? "").toString().slice(0, 5);

  const mapStateOperation = (carrierOperation) => {
    const desc = ((carrierOperation && carrierOperation.carrierOperationDesc) || "").toLowerCase();
    if (desc.includes("interstate")) return "interstate";
    if (desc.includes("intrastate")) return "intrastate";
    return undefined;
  };

  const validateDotNumber = async () => {
    await trigger("step2.dot");
  };

  const getInfoUsingDotNumber = debounce(async (dot) => {
    if (!dot) return;

    try {
      const response = await axios.get(
        `/api/dot-number/${dot}`,
        { timeout: 15000 }
      );
      const carrier = response?.data?.data?.content?.carrier;
      if (!carrier) return;

      // ---- Backfill Step 2 (only if empty) ----
      if (isEmpty(insuredName) && !isEmpty(carrier.legalName)) {
        setValue("step2.insuredName", carrier.legalName, { shouldDirty: true });
      }
      if (isEmpty(dba) && !isEmpty(carrier.dbaName)) {
        setValue("step2.dba", carrier.dbaName, { shouldDirty: true });
      }

      // If hasUsdot is yes and dotNumber field is empty, set it from API
      if (hasUsdot === "yes" && isEmpty(dotNumber) && carrier.dotNumber) {
        setValue("step2.dot.dotNumber", Number(carrier.dotNumber), { shouldDirty: true });
      }

      // Physical address → mailing (set only empty)
      const addr1 = (carrier.phyStreet || "").toString().trim();
      const city = (carrier.phyCity || "").toString().trim();
      const state = (carrier.phyState || "").toString().trim();
      const zip5 = normalizeZip5(carrier.phyZipcode);

      if (!isEmpty(addr1) || !isEmpty(city) || !isEmpty(state) || !isEmpty(zip5)) {
        if (isEmpty(mAddr1) && !isEmpty(addr1)) setValue("step2.mailingAddress.address1", addr1, { shouldDirty: true });
        if (isEmpty(mCity) && !isEmpty(city)) setValue("step2.mailingAddress.city", city, { shouldDirty: true });
        if (isEmpty(mState) && !isEmpty(state)) setValue("step2.mailingAddress.state", state, { shouldDirty: true });
        if (isEmpty(mZip) && !isEmpty(zip5)) setValue("step2.mailingAddress.zip", zip5, { shouldDirty: true });

        // If garaging is entirely empty, mirror physical to garaging
        const garagingAllEmpty = [gAddr1, gCity, gState, gZip].every(isEmpty);
        if (garagingAllEmpty) {
          if (!isEmpty(addr1)) setValue("step2.garagingAddress.address1", addr1, { shouldDirty: true });
          if (!isEmpty(city)) setValue("step2.garagingAddress.city", city, { shouldDirty: true });
          if (!isEmpty(state)) setValue("step2.garagingAddress.state", state, { shouldDirty: true });
          if (!isEmpty(zip5)) setValue("step2.garagingAddress.zip", zip5, { shouldDirty: true });

          // If we fully mirrored, set the flag when not already set
          // if (!isEmpty(addr1) && !isEmpty(city) && !isEmpty(state) && !isEmpty(zip5)) {
          //   setValue("step2.garagingSameAsMailing", true, { shouldDirty: true });
          // }
        }
      }

      // ---- Backfill Step 8: stateOperation (only if empty) ----
      const sop = mapStateOperation(carrier.carrierOperation);
      if (sop && isEmpty(stateOperation)) {
        setValue("step8.stateOperation", sop, { shouldDirty: true });
      }
    } catch (error) {
      console.error("Error fetching DOT number info:", error);
    }
  }, 1000);

  useEffect(() => {
    if (dotNumber && !hasUsdot) {
      getInfoUsingDotNumber(dotNumber);
      setValue("step2.dot.hasUsdot", "yes", { shouldDirty: true });
    }
    validateDotNumber();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dotNumber, hasUsdot]);

  return (
    <>
      {/* USDOT? */}
      <div className="space-y-2">
        <Label>Does the customer have a USDOT Number?{requiresDot ? " *" : ""}</Label>
        <Controller
          control={control}
          name="step2.dot.hasUsdot"
          render={({ field }) => (
            <Select
              value={field.value ?? ""}
              onValueChange={(v) => {
                field.onChange(v);
                // If switched to "yes" and DOT already present, fetch now
                const currentDot = dotNumber;
                if (v === "yes" && currentDot) {
                  getInfoUsingDotNumber(currentDot);
                }
              }}
              onOpenChange={() => {
                field.onChange(field.value);
              }}
            >
              <SelectTrigger className={"w-full"}>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent className="max-h-60 overflow-y-auto">
                <SelectGroup>
                  <SelectLabel>Choose</SelectLabel>
                  {YES_NO.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          )}
        />
        <FieldError name="step2.dot.hasUsdot" step="step2" control={control} />
        <p className="text-xs text-gray-500">
          With no DOT number, you can only quote with Progressive.
        </p>
      </div>

      {/* DOT Number */}
      <div className="space-y-2">
        <Label htmlFor="dotNumber">DOT Number{requiresDot ? " *" : ""}</Label>
        <Input
          id="dotNumber"
          placeholder="Enter..."
          {...register("step2.dot.dotNumber", {
            onChange: (e) => {
              const v = e?.target?.value;
              // fire lookup when number looks valid-ish
              const n = Number(v);
              if (Number.isFinite(n) && n >= 1 && n <= 999999999 && hasUsdot === "yes") {
                getInfoUsingDotNumber(n);
              }
            },
          })}
        />
        <FieldError name="step2.dot.dotNumber" step="step2" control={control} />
        <p className="text-xs text-gray-500">
          The “DOT Number” issued by the federal motor safety administration
        </p>
      </div>
    </>
  );
}
